import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { CircularProgress, makeStyles } from '@material-ui/core'
import { useQueryClient } from '@tanstack/react-query'
import ItemStatusEditor from '../../../../ItemStatusEditor'
import MetadataEditor from '../../../../MetadataEditor'
import { useAssignedAttachmentsByClient } from '../../../../../../../api/documentVault'
import { useAppContext } from '../../../../../../../redux/slices/appContext'
import {
  useAddComment,
  useCommentThreadDetails,
  useCreateCommentThread,
  useEditComment,
  useRemoveComment
} from '../../../../../../../api/comments'
import { useConfigAtDepth } from '../../../../hooks'
import StatusDisplayBoardLayout from '../StatusDisplayBoardLayout'
import SydButton from '../../../../../../commonDesign/Button'
import { useStatusReportContext } from '../../../../statusReportContext'
import StatusReportItemSupplementalContent from '../../../../StatusReportItemSupplementalContent'
import Attachment from './Attachment'
import CommentList from './CommentList'

const useStyles = makeStyles(() => ({
  '@keyframes slideInFromRight': {
    '0%': {
      opacity: 0,
      transform: 'translateX(100%)'
    },
    '100%': {
      opacity: 1,
      transform: 'translateX(0)'
    }
  },
  '@keyframes slideOutToRight': {
    '0%': {
      opacity: 1,
      transform: 'translateX(0)'
    },
    '100%': {
      opacity: 0,
      transform: 'translateX(100%)'
    }
  },
  detailPanel: {
    position: 'sticky',
    top: 'var(--dim-4)',
    alignSelf: 'flex-start',
    flex: '0 0 400px',
    maxHeight: ({ dynamicMaxHeight }) => dynamicMaxHeight || 'calc(100vh - var(--dim-4) * 4)',
    minHeight: '300px',
    borderRadius: 'var(--border-radius-lg)',
    background: 'var(--color-neutral-100)',
    border: 'var(--border-neutral-thin)',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    animation: ({ isClosing }) => isClosing
      ? '$slideOutToRight 0.25s ease-in forwards'
      : '$slideInFromRight 0.3s ease-out'
  },
  detailPanelTitle: {
    flexShrink: 0,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: 'var(--border-neutral-thin)',
    padding: 'var(--dim-2) var(--dim-4)',
    color: 'var(--color-neutral-800)'
  },
  detailPanelContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-4)',
    flex: 1,
    minHeight: 0,
    overflowY: 'auto',
    padding: 'var(--dim-4)'
  },
  detailList: {
    display: 'flex',
    flexDirection: 'column',
    '& .__detail-item': {
      display: 'flex',
      gap: 'var(--dim-4)',
      padding: 'var(--dim-2) 0',
      alignItems: 'center'
    },
    '& .__detail-item:not(:last-child)': {
      borderBottom: 'var(--border-neutral-thin)'
    },
    '& .__detail-item-header': {
      width: '100px',
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      margin: 0
    },
    '& .__detail-item-content': {
      flex: 1
    }
  },
  commentsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: 'var(--dim-3)',
    flex: 1,
    minHeight: '300px',
    '& .__comments-header': {
      fontSize: 'var(--font-size-xs)',
      color: 'var(--color-neutral-800)',
      fontWeight: 'var(--font-weight-light)',
      margin: 0
    }
  }
}))

function StatusReportDetailsPanel ({
  item,
  config,
  onClose,
  onUpdate
}) {
  const { statuses, disableCommenting } = useStatusReportContext()
  const panelRef = useRef(null)
  const [dynamicMaxHeight, setDynamicMaxHeight] = useState(null)
  const [threadKey, setThreadKey] = useState(item?.threadKey || null)
  const itemConfig = useConfigAtDepth(config, item?.depth || 0)

  useEffect(() => {
    setThreadKey(item?.threadKey || null)
  }, [item?.threadKey])

  // Calculate dynamic max height based on panel's position
  useEffect(() => {
    let rafId = null

    const calculateMaxHeight = () => {
      if (panelRef.current) {
        const rect = panelRef.current.getBoundingClientRect()
        const topOffset = rect.top
        const bottomMargin = 16 // Additional margin from bottom
        const calculatedMaxHeight = `${window.innerHeight - topOffset - bottomMargin}px`
        setDynamicMaxHeight(calculatedMaxHeight)
      }
    }

    const handleScroll = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(calculateMaxHeight)
    }

    const handleResize = () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      rafId = requestAnimationFrame(calculateMaxHeight)
    }

    calculateMaxHeight()
    window.addEventListener('scroll', handleScroll, true) // Use capture phase
    window.addEventListener('resize', handleResize)
    return () => {
      if (rafId) {
        cancelAnimationFrame(rafId)
      }
      window.removeEventListener('scroll', handleScroll, true)
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  const classes = useStyles({ dynamicMaxHeight })
  const { clientId, userId } = useAppContext()
  const queryClient = useQueryClient()

  const { data: attachments, isLoading: attachmentsLoading, refetch: refetchAttachments } = useAssignedAttachmentsByClient(278, item.statusTemplateItemId, clientId)

  const { data: commentData, isLoading: commentsLoading, refetch: refetchComments } = useCommentThreadDetails(
    threadKey,
    278,
    item.statusTemplateItemId,
    clientId
  )

  const { mutateAsync: createThreadAsync, isLoading: createThreadIsLoading } = useCreateCommentThread()
  const { mutateAsync: addCommentAsync, isLoading: addCommentIsLoading } = useAddComment()
  const { mutateAsync: editCommentAsync, isLoading: editCommentIsLoading } = useEditComment()
  const { mutateAsync: removeCommentAsync, isLoading: removeCommentIsLoading } = useRemoveComment()

  // The hook may resolve a thread via assignment lookup even when local threadKey is null.
  const effectiveThreadKey = useMemo(
    () => commentData?.thread?.threadKey || threadKey,
    [commentData?.thread?.threadKey, threadKey]
  )

  const isLoading = useMemo(() => !!item.statusTemplateItemId && (attachmentsLoading || (!!item.threadKey && commentsLoading)), [item.statusTemplateItemId, attachmentsLoading, commentsLoading, item.threadKey])

  // Transform comment data to match our component's expected format
  const comments = useMemo(() => {
    if (!commentData?.comments) return []

    return commentData.comments.map(comment => {
      const author = commentData.authors?.find(a => a.userId === comment.author)
      return {
        commentId: comment.commentKey,
        text: comment.body,
        createdDate: comment.publishDate,
        userId: comment.author,
        userName: author?.authorName || 'Unknown User',
        userAvatar: author?.avatarUrl || null,
        avatarType: author?.avatarType || 'media'
      }
    })
  }, [commentData])

  const handleAddComment = useCallback(async (text) => {
    if (!text.trim()) return

    try {
      if (effectiveThreadKey) {
        await addCommentAsync({
          threadKey: effectiveThreadKey,
          body: text
        })
      } else {
        const threadData = await createThreadAsync({
          clientId,
          levelTypeId: 278,
          levelId: item.statusTemplateItemId,
          body: text
        })
        setThreadKey(threadData.threadKey)
        onUpdate?.()
      }

      await refetchComments()
    } catch (error) {
      console.error('Failed to add comment:', error)
      // Rethrow so CommentInput preserves the unsaved text instead of clearing.
      throw error
    }
  }, [item, refetchComments, addCommentAsync, createThreadAsync, clientId, onUpdate, effectiveThreadKey])

  const handleEditComment = useCallback(async (commentKey, body) => {
    if (!body?.trim() || !effectiveThreadKey) return

    try {
      await editCommentAsync({
        commentKey,
        threadKey: effectiveThreadKey,
        body: body.trim()
      })
      await refetchComments()
    } catch (error) {
      console.error('Failed to edit comment:', error)
      // Rethrow so the editor stays open for retry.
      throw error
    }
  }, [editCommentAsync, refetchComments, effectiveThreadKey])

  const handleDeleteComment = useCallback(async (commentKey) => {
    if (!effectiveThreadKey) return

    try {
      const result = await removeCommentAsync({ commentKey, threadKey: effectiveThreadKey })
      if (result?.threadDeleted) {
        onUpdate?.()
        // Drop both cache entries for THIS item so the by-assignment query that
        // takes over (after setThreadKey(null)) starts empty instead of painting
        // the pre-delete comment list, and so react-query doesn't auto-refetch
        // the by-key entry against the now-deleted thread (500). Targeted to
        // this item so other open comment threads aren't evicted.
        const baseKey = ['get-comment-thread-details', userId]
        const tailKey = [278, item.statusTemplateItemId, clientId]
        queryClient.removeQueries({ queryKey: [...baseKey, effectiveThreadKey, ...tailKey], exact: true })
        queryClient.removeQueries({ queryKey: [...baseKey, null, ...tailKey], exact: true })
        setThreadKey(null)
        return
      }
      await refetchComments()
    } catch (error) {
      console.error('Failed to delete comment:', error)
      // Rethrow so the confirm modal stays open for retry.
      throw error
    }
  }, [removeCommentAsync, refetchComments, effectiveThreadKey, onUpdate, queryClient, userId, clientId, item.statusTemplateItemId])

  if (!item) return null

  return (
    <div ref={panelRef} className={classes.detailPanel}>
      {item?.parent && (
        <div className={classes.detailPanelTitle}>
          {item?.parent?.displayName}

          <SydButton
            size='xs'
            variant='secondary'
            onClick={onClose}
          >
            Close
          </SydButton>
        </div>
      )}
      <div className={classes.detailPanelContent}>
        <div className='flex flex-col gap-2'>
          <StatusDisplayBoardLayout statusInfo={item.statusInfo} size={30} useIcon hasBackground />
          <p className='font-size-lg m-0'>{item.displayName}</p>
          {!!item.description?.length && (
            <p className='font-size-xs font-light m-0'>{item.description}</p>
          )}
        </div>
        {isLoading ? (
          <div className='flex-1 flex items-center justify-center'>
            <CircularProgress />
          </div>
        ) : (
          <>
            <div className={classes.detailList}>
              <div className='__detail-item'>
                <p className='__detail-item-header'>Status</p>
                <div className='__detail-item-content'>
                  <ItemStatusEditor
                    item={item}
                    statusInfo={item.statusInfo}
                    statuses={statuses}
                    isFetching={false}
                    style={{ padding: 0 }}
                  />
                </div>
              </div>
              {!!attachments?.attachments?.length && (
                <div className='__detail-item'>
                  <p className='__detail-item-header'>Attachments</p>
                  <div className='__detail-item-content'>
                    {attachments.attachments?.map((attachment) => (
                      <Attachment key={attachment.attachmentId} attachment={attachment} onRemoveAttachment={refetchAttachments} />
                    ))}
                  </div>
                </div>
              )}
              {itemConfig?.showMetadata && !!item?.metadata?.length && (
                <>
                  {item.metadata.map((meta) => {
                    // Format camelCase key to Title Case (e.g., "nextReviewDate" -> "Next Review Date")
                    const displayLabel = meta.displayName || meta.metadataKey
                      .replace(/([A-Z])/g, ' $1')
                      .replace(/^./, (str) => str.toUpperCase())
                      .trim()

                    return (
                      <div key={meta.metadataKey} className='__detail-item'>
                        <p className='__detail-item-header'>{displayLabel}</p>
                        <div className='__detail-item-content'>
                          <MetadataEditor
                            item={item}
                            metadataKey={meta.metadataKey}
                            metadataType={meta.valueType}
                            currentValue={meta.value}
                          />
                        </div>
                      </div>
                    )
                  })}
                </>
              )}
            </div>

            <StatusReportItemSupplementalContent item={item} />

            {!!item.statusTemplateItemId && !disableCommenting && (
              <div className={classes.commentsContainer}>
                <p className='__comments-header'>Comments</p>
                <CommentList
                  key={item.statusTemplateItemId}
                  comments={comments}
                  onAddComment={handleAddComment}
                  onEditComment={handleEditComment}
                  onDeleteComment={handleDeleteComment}
                  processing={createThreadIsLoading || addCommentIsLoading || editCommentIsLoading || removeCommentIsLoading}
                />
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

StatusReportDetailsPanel.propTypes = {
  item: PropTypes.shape({
    statusReportItemId: PropTypes.number,
    statusTemplateItemId: PropTypes.number,
    displayName: PropTypes.string,
    description: PropTypes.string,
    depth: PropTypes.number,
    metadata: PropTypes.arrayOf(PropTypes.shape({
      metadataKey: PropTypes.string,
      valueType: PropTypes.string,
      value: PropTypes.any,
      displayName: PropTypes.string,
      getValue: PropTypes.func
    })),
    statusInfo: PropTypes.shape({
      color: PropTypes.string,
      displayName: PropTypes.string,
      icon: PropTypes.string
    }),
    threadKey: PropTypes.string,
    parent: PropTypes.shape({
      displayName: PropTypes.string
    })
  }),
  config: PropTypes.object,
  onClose: PropTypes.func,
  onUpdate: PropTypes.func
}

export default StatusReportDetailsPanel
